import { format as stringFormat } from '@/utils/string'
import { TFnType } from '@tolgee/react'

export const toDeliveryAddress = (
  address: Oms.DeliveryAddresses
): Oms.UpdateDeliveryLocationPayload => ({
  selectedAddressId: address.id,
  deliveryAddress: address.autoCompleteAddress,
  deliveryAddressOne: address.address,
  deliveryAddressTwo: address.addressTwo,
  deliveryAddressCity: address.city,
  deliveryAddressState: address.state,
  deliveryAddressCountry: address.country,
  deliveryAddressPostal: address.postal,
  latitude: address.latitude,
  longitude: address.longitude,
  addressTwoDetails: address.addressTwoDetails,
})

export const storeOrderToDeliveryAddress = (
  storeOrder: Oms.StoreOrder
): Oms.UpdateDeliveryLocationPayload => ({
  deliveryAddress:
    storeOrder.deliveryAddressAutoComplete || storeOrder.deliveryAddress,
  deliveryAddressOne: storeOrder.deliveryAddressOne,
  deliveryAddressTwo: storeOrder.deliveryAddressTwo,
  deliveryAddressCity: storeOrder.deliveryAddressCity,
  deliveryAddressState: storeOrder.deliveryAddressState,
  deliveryAddressCountry: storeOrder.deliveryAddressCountry,
  deliveryAddressPostal: storeOrder.deliveryAddressPostal,
  latitude: storeOrder.customerLatitude,
  longitude: storeOrder.customerLongitude,
  addressTwoDetails: storeOrder.addressTwoDetails,
})

export const convertAddressSearchInput = ({
  address,
  addressTwo = '',
  menuCountry,
}: {
  address: MapAddress.AddressSearchResult
  addressTwo?: string | null
  menuCountry?: string
}): Oms.UpdateDeliveryLocationPayload => {
  return {
    deliveryAddress: address.formattedAddress,
    deliveryAddressOne: address.addressOne,
    deliveryAddressTwo: addressTwo,
    deliveryAddressState: address.state,
    deliveryAddressCity: address.city,
    deliveryAddressPostal: address.postal,
    deliveryAddressCountry: menuCountry,
    latitude: address.latitude,
    longitude: address.longitude,
    addressTwoDetails: address.addressTwoDetails,
  }
}

const addressMap = {
  addressOne: 'address',
  addressTwo: 'addressTwo',
  city: 'addressCity',
  state: 'addressState',
  postal: 'addressPostal',
  country: 'addressCountry',
} as Record<string, string>

export const getOrderedAddressFields = (
  addressFormat = '',
  addressRequiredFields = ''
) => {
  const orderedAddressFields = [] as {
    field: string
    isRequired: boolean
  }[]

  // Use regex to capture the element inside the {{ }} and put it into an array
  const addressFields = [] as string[]
  stringFormat(addressFormat, (_, field) => {
    addressFields.push(field)
    return field
  })

  const requiredFields = addressRequiredFields
    .replace(/Header/g, '')
    .replace(/ /g, '')
    .split(',')

  addressFields.map((field) =>
    orderedAddressFields.push({
      field,
      isRequired: Boolean(
        addressMap[field] && requiredFields.includes(addressMap[field])
      ),
    })
  )
  return orderedAddressFields
}

interface AddressConfig {
  heading: string
  optional: string
  billingFieldName: string
  deliveryFieldName: string
}

export const addressConfig = {
  addressOne: (t: TFnType): AddressConfig => ({
    heading: t('Street Address'),
    optional: t('Street Address (Optional)'),
    billingFieldName: 'billingAddressOne',
    deliveryFieldName: 'deliveryAddressOne',
  }),
  addressTwo: (t: TFnType): AddressConfig => ({
    heading: t('Unit'),
    optional: t('Unit (Optional)'),
    billingFieldName: 'billingAddressTwo',
    deliveryFieldName: 'deliveryAddressTwo',
  }),
  city: (t: TFnType): AddressConfig => ({
    heading: t('City'),
    optional: t('City (Optional)'),
    billingFieldName: 'billingAddressCity',
    deliveryFieldName: 'deliveryAddressCity',
  }),
  state: (t: TFnType): AddressConfig => ({
    heading: t('State'),
    optional: t('State (Optional)'),
    billingFieldName: 'billingAddressState',
    deliveryFieldName: 'deliveryAddressState',
  }),
  postal: (t: TFnType): AddressConfig => ({
    heading: t('Postal Code'),
    optional: t('Postal Code (Optional)'),
    billingFieldName: 'billingAddressPostal',
    deliveryFieldName: 'deliveryAddressPostal',
  }),
  country: (t: TFnType): AddressConfig => ({
    heading: t('Country'),
    optional: t('Country'),
    billingFieldName: 'deliveryAddressCountry',
    deliveryFieldName: 'deliveryAddressCountry',
  }),
} as Record<string, (t: TFnType) => AddressConfig>
